import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Citation } from '@/types/chat.ts';
import type { ChatDocument, ChatDocumentPanelSettings } from '@/store/slices/chat/chat';
import { selectUnviewableDocumentIds, useAppSelector } from '@/store';
import { useChatContext } from '@/ui/features/chat/useChatContext';
import { CitationButton } from '@/ui/features/chat/components/CitationButton.tsx';
import { UnviewableWarningCell } from '@/ui/components/UnviewableWarningCell';
import { UNVIEWABLE_CHAT_DOCUMENT_TOOLTIP } from '@/ui/features/chat/utils/unviewableChatDocument';
import { isDisplayableDocument } from '@/utils/getDocumentViewerKind';
import { UnpinIcon } from '@repo/atomic-components';
import { HeaderIconButton } from '@/ui/pages/ChatPage/components/HeaderIconButton';
import { isAiGeneratedCitation } from '@/utils';

const SCROLL_MARGIN_PX = 16;

interface CitationsPanelProps {
  citations: (Citation & { id: string })[];
  orderedUuids: string[];
  /** Overrides the citation click. Chat uses it to keep the draft editor open. */
  onCitationOpen?: (settings: ChatDocumentPanelSettings, documentName?: string) => void;
  documents: ChatDocument[];
  /** Wire this to the resizable panel's imperative `collapse()` — hides the panel entirely. */
  onCollapse?: () => void;
  /** Highlights a citation and scrolls to its document; a new `trigger` scrolls again. */
  highlightedCitation?: { id: string; trigger: number } | null;
}

export const CitationsPanel = ({
  citations,
  orderedUuids,
  onCitationOpen,
  documents,
  onCollapse,
  highlightedCitation,
}: CitationsPanelProps) => {
  const { handleCitedDocumentClick } = useChatContext();
  const unviewableDocumentIds = useAppSelector(selectUnviewableDocumentIds);
  const openCitation =
    onCitationOpen ??
    ((settings: ChatDocumentPanelSettings, documentName?: string) =>
      void handleCitedDocumentClick(settings, documentName));

  const idOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    orderedUuids.forEach((id, index) => {
      map.set(id, index);
    });
    return map;
  }, [orderedUuids]);

  const getOrderIndex = useCallback(
    (id: string): number => {
      const index = idOrderMap.get(id);
      return typeof index === 'number' ? index : Number.MAX_SAFE_INTEGER;
    },
    [idOrderMap],
  );

  const citationsByDocumentId = useMemo(() => {
    const map = new Map<string, (Citation & { id: string })[]>();
    citations.forEach((citation) => {
      // Every group is headed by a document, so a citation without one is not listable here.
      if (!citation.documentId) return;
      const list = map.get(citation.documentId) ?? [];
      list.push(citation);
      map.set(citation.documentId, list);
    });

    // Sort each document's citations by their global (persistent) order.
    map.forEach((list, documentId) => {
      map.set(
        documentId,
        [...list].sort((a, b) => getOrderIndex(a.id) - getOrderIndex(b.id)),
      );
    });

    return map;
  }, [citations, getOrderIndex]);

  const orderedDocuments = useMemo(() => {
    const minCitationIndexForDoc = (documentId: string) => {
      const docCitations = citationsByDocumentId.get(documentId);
      if (!docCitations?.length) return Number.MAX_SAFE_INTEGER;
      return getOrderIndex(docCitations[0].id);
    };

    return [...documents].sort((a, b) => minCitationIndexForDoc(a.documentId) - minCitationIndexForDoc(b.documentId));
  }, [citationsByDocumentId, documents, getOrderIndex]);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const highlightedDocumentId = highlightedCitation
    ? citations.find((citation) => citation.id === highlightedCitation.id)?.documentId
    : null;

  useEffect(() => {
    if (!highlightedCitation || !highlightedDocumentId) return;
    // A frame later, so a panel that has just opened has its width.
    const frame = requestAnimationFrame(() => {
      const scrollArea = scrollAreaRef.current;
      const group = scrollArea?.querySelector<HTMLElement>(
        `[data-source-document="${CSS.escape(highlightedDocumentId)}"]`,
      );
      if (!scrollArea || !group) return;
      const areaRect = scrollArea.getBoundingClientRect();
      const groupRect = group.getBoundingClientRect();
      if (groupRect.top >= areaRect.top && groupRect.bottom <= areaRect.bottom) return;
      // Only the panel scrolls; `scrollIntoView` would also move the page's overflow-hidden containers.
      scrollArea.scrollTo({ top: scrollArea.scrollTop + groupRect.top - areaRect.top - SCROLL_MARGIN_PX });
    });
    return () => cancelAnimationFrame(frame);
  }, [highlightedCitation, highlightedDocumentId]);

  return (
    /* `relative` keeps the sr-only warning in the panel; otherwise it scrolls the app shell at 200% zoom. */
    <div className="bg-theme-white text-theme-black relative flex h-full w-full flex-col">
      <div className="border-theme-border-grey flex h-14 w-full shrink-0 items-center justify-between border-b px-4 lg:px-6">
        <div className="text-[1.0625rem] font-semibold">Sources</div>
        {onCollapse && (
          <HeaderIconButton icon={<UnpinIcon width={16} height={16} />} tooltip="Hide sources" onClick={onCollapse} />
        )}
      </div>
      <div ref={scrollAreaRef} className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4 lg:p-6">
        {orderedDocuments.map((document) => {
          const docName = document.documentName;
          const docCitations = citationsByDocumentId.get(document.documentId) ?? [];

          if (docCitations.length === 0) return null;

          const isUnviewable = !isDisplayableDocument(document) || !!unviewableDocumentIds[document.documentId];

          return (
            <div key={document.documentId} className="flex flex-col gap-2" data-source-document={document.documentId}>
              <div className="flex items-center gap-2">
                <div
                  className={`line-clamp-2 text-sm font-semibold break-words ${isUnviewable ? 'text-theme-text-grey' : ''}`}
                  title={docName}
                >
                  {docName}
                </div>
                {isUnviewable && <UnviewableWarningCell reason={UNVIEWABLE_CHAT_DOCUMENT_TOOLTIP} />}
              </div>
              <div className="flex flex-wrap gap-2">
                {docCitations.map((citation) => {
                  const number = getOrderIndex(citation.id) + 1;
                  return (
                    <CitationButton
                      key={citation.id}
                      onClick={() =>
                        openCitation(
                          {
                            open: true,
                            pageNumber: citation.pageNumber ?? 1,
                            documentId: document.documentId,
                            highlightText: citation.extract,
                            highlightTrigger: Date.now(),
                          },
                          docName,
                        )
                      }
                      number={number}
                      citationText={citation.extract}
                      documentName={docName}
                      pageNumber={citation.pageNumber ?? undefined}
                      muted={isUnviewable}
                      isAiImageDescription={isAiGeneratedCitation(citation)}
                      highlighted={citation.id === highlightedCitation?.id}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
